/**
 * Closed-enum label resolvers for values that live in match data (not JSX) and
 * must localize through the catalog. Kept out of the components so every render
 * site shares one source of truth — and one fallback policy.
 */
import type { TFunction } from 'i18next';

// Catalog key per canonical FIFA stage name (lowercased). Multiple raw spellings
// can map to the same key (e.g. "quarter-finals" / "quarter finals").
const STAGE_KEYS: Record<string, string> = {
  friendly: 'match:stage.friendly',
  'round of 32': 'match:stage.roundOf32',
  'round of 16': 'match:stage.roundOf16',
  'quarter-finals': 'match:stage.quarterFinals',
  'quarter finals': 'match:stage.quarterFinals',
  'semi-finals': 'match:stage.semiFinals',
  'semi finals': 'match:stage.semiFinals',
  final: 'match:stage.final',
  'third place': 'match:stage.thirdPlace',
  'third-place play-off': 'match:stage.thirdPlace',
};

/**
 * Localize a match stage string.
 *   - "Group A".."Group L" → "Group {{letter}}" with the letter uppercased.
 *   - A known FIFA stage (case-insensitive) → its catalog value.
 *   - Anything else → the raw `stage` string verbatim. We never return a bare
 *     key or a blank: unknown stages (new formats, dirty data) stay readable.
 */
export function stageLabel(stage: string, t: TFunction): string {
  const groupMatch = /^group\s+([a-l])$/i.exec(stage);
  if (groupMatch) {
    return t('match:stage.group', { letter: groupMatch[1]!.toUpperCase() });
  }
  const key = STAGE_KEYS[stage.trim().toLowerCase()];
  if (key) return t(key);
  return stage;
}
